import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function SolutionsSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Solutions");
  const [heroHeading, setHeroHeading] = useState(
    "Comprehensive Digital Solutions for",
  );
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState(
    "Australian Businesses",
  );
  const [heroDescription, setHeroDescription] = useState("");
  const [heroVideo, setHeroVideo] = useState("");
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [heroSecondary, setHeroSecondary] = useState(emptyButton());
  const [trustBadges, setTrustBadges] = useState([
    { icon: "Award", title: "SAP Partner", sub: "Certified Experts" },
    { icon: "Users", title: "150+ Clients", sub: "Across Australia" },
    { icon: "Shield", title: "60+ Years", sub: "Combined Experience" },
  ]);

  /* ---------------- SOLUTION SNAPSHOT ---------------- */
  const [snapActive, setSnapActive] = useState(true);
  const [snapBadgeText, setSnapBadgeText] = useState("Solution Snapshot");
  const [snapHeading, setSnapHeading] = useState(
    "Solve Your Business Challenges with",
  );
  const [snapHeadingEmphasis, setSnapHeadingEmphasis] = useState(
    "Integrated Solutions",
  );
  const [snapDescription, setSnapDescription] = useState("");
  const [snapCTAText, setSnapCTAText] = useState("See How We Help");
  const [snapCTALink, setSnapCTALink] = useState("#solutions");

  /* ---------------- SOLUTIONS BY TECHNOLOGY ---------------- */
  const [techActive, setTechActive] = useState(true);
  const [techBadgeText, setTechBadgeText] = useState("Key Solutions");
  const [techHeading, setTechHeading] = useState("Solutions by");
  const [techHeadingEmphasis, setTechHeadingEmphasis] = useState("Technology");
  const [techCards, setTechCards] = useState([
    { title: "", description: "", href: "", icon: "" },
  ]);

  /* ---------------- SOLUTIONS BY ROLE ---------------- */
  const [roleActive, setRoleActive] = useState(true);
  const [roleBadgeText, setRoleBadgeText] = useState("By Role");
  const [roleHeading, setRoleHeading] = useState("Solutions by");
  const [roleHeadingEmphasis, setRoleHeadingEmphasis] = useState("Role");
  const [roleCards, setRoleCards] = useState([
    { title: "", href: "", icon: "" },
  ]);

  /* ---------------- SOLUTIONS BY INDUSTRY ---------------- */
  const [indActive, setIndActive] = useState(true);
  const [indBadgeText, setIndBadgeText] = useState("By Industry");
  const [indHeading, setIndHeading] = useState("Solutions by");
  const [indHeadingEmphasis, setIndHeadingEmphasis] = useState("Industry");
  const [indCards, setIndCards] = useState([
    { title: "", href: "", icon: "", gradient: "" },
  ]);

  /* ---------------- WHY THINKPROCESS ---------------- */
  const [whyActive, setWhyActive] = useState(true);
  const [whyBadgeText, setWhyBadgeText] = useState("Why ThinkProcess");
  const [whyHeading, setWhyHeading] = useState("Your Strategic");
  const [whyHeadingEmphasis, setWhyHeadingEmphasis] = useState("Partner");
  const [whyCards, setWhyCards] = useState([{ title: "", description: "" }]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState(
    "Ready to Transform Your Operations?",
  );
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState(emptyButton());

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      setHeroVideo(d.hero.video || "");
      if (d.hero.primary_cta)
        setHeroPrimary({
          text: d.hero.primary_cta.text || "",
          url: d.hero.primary_cta.link || "",
          newTab: false,
        });
      if (d.hero.secondary_cta)
        setHeroSecondary({
          text: d.hero.secondary_cta.text || "",
          url: d.hero.secondary_cta.link || "",
          newTab: false,
        });
      if (d.hero.trust_badges?.length) setTrustBadges(d.hero.trust_badges);
    }

    if (d.solution_snapshot) {
      setSnapBadgeText(d.solution_snapshot.badge_text || "");
      setSnapHeading(d.solution_snapshot.heading || "");
      setSnapHeadingEmphasis(d.solution_snapshot.heading_emphasis || "");
      setSnapDescription(d.solution_snapshot.description || "");
      setSnapCTAText(d.solution_snapshot.cta_text || "");
      setSnapCTALink(d.solution_snapshot.cta_link || "");
    }

    if (d.solutions_by_technology) {
      setTechBadgeText(d.solutions_by_technology.badge_text || "");
      setTechHeading(d.solutions_by_technology.heading || "");
      setTechHeadingEmphasis(d.solutions_by_technology.heading_emphasis || "");
      if (d.solutions_by_technology.cards?.length)
        setTechCards(d.solutions_by_technology.cards);
    }

    if (d.solutions_by_role) {
      setRoleBadgeText(d.solutions_by_role.badge_text || "");
      setRoleHeading(d.solutions_by_role.heading || "");
      setRoleHeadingEmphasis(d.solutions_by_role.heading_emphasis || "");
      if (d.solutions_by_role.cards?.length)
        setRoleCards(d.solutions_by_role.cards);
    }

    if (d.solutions_by_industry) {
      setIndBadgeText(d.solutions_by_industry.badge_text || "");
      setIndHeading(d.solutions_by_industry.heading || "");
      setIndHeadingEmphasis(d.solutions_by_industry.heading_emphasis || "");
      if (d.solutions_by_industry.cards?.length)
        setIndCards(d.solutions_by_industry.cards);
    }

    if (d.why_thinkprocess) {
      setWhyBadgeText(d.why_thinkprocess.badge_text || "");
      setWhyHeading(d.why_thinkprocess.heading || "");
      setWhyHeadingEmphasis(d.why_thinkprocess.heading_emphasis || "");
      if (d.why_thinkprocess.cards?.length)
        setWhyCards(d.why_thinkprocess.cards);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaDescription(d.final_cta.description || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({
          text: d.final_cta.primary_cta.text || "",
          url: d.final_cta.primary_cta.link || "",
          newTab: false,
        });
      if (d.final_cta.secondary_cta)
        setCtaSecondary({
          text: d.final_cta.secondary_cta.text || "",
          url: d.final_cta.secondary_cta.link || "",
          newTab: false,
        });
    }
  }, [initialData]);

  const buildPayload = () => ({
    p_id: 0,
    page_name: "solutions",
    page_type: "solutions",
    category: "website",
    sections: {
      hero: {
        badge_text: heroBadgeText,
        heading: heroHeading,
        heading_emphasis: heroHeadingEmphasis,
        description: heroDescription,
        video: heroVideo,
        primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
        secondary_cta: { text: heroSecondary.text, link: heroSecondary.url },
        trust_badges: trustBadges,
      },
      solution_snapshot: {
        badge_text: snapBadgeText,
        heading: snapHeading,
        heading_emphasis: snapHeadingEmphasis,
        description: snapDescription,
        cta_text: snapCTAText,
        cta_link: snapCTALink,
      },
      solutions_by_technology: {
        badge_text: techBadgeText,
        heading: techHeading,
        heading_emphasis: techHeadingEmphasis,
        cards: techCards,
      },
      solutions_by_role: {
        badge_text: roleBadgeText,
        heading: roleHeading,
        heading_emphasis: roleHeadingEmphasis,
        cards: roleCards,
      },
      solutions_by_industry: {
        badge_text: indBadgeText,
        heading: indHeading,
        heading_emphasis: indHeadingEmphasis,
        cards: indCards,
      },
      why_thinkprocess: {
        badge_text: whyBadgeText,
        heading: whyHeading,
        heading_emphasis: whyHeadingEmphasis,
        cards: whyCards,
      },
      final_cta: {
        heading: ctaHeading,
        description: ctaDescription,
        primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
        secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
      },
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload().sections);
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroVideo,
    heroPrimary,
    heroSecondary,
    trustBadges,
    snapBadgeText,
    snapHeading,
    snapHeadingEmphasis,
    snapDescription,
    snapCTAText,
    snapCTALink,
    techBadgeText,
    techHeading,
    techHeadingEmphasis,
    techCards,
    roleBadgeText,
    roleHeading,
    roleHeadingEmphasis,
    roleCards,
    indBadgeText,
    indHeading,
    indHeadingEmphasis,
    indCards,
    whyBadgeText,
    whyHeading,
    whyHeadingEmphasis,
    whyCards,
    ctaHeading,
    ctaDescription,
    ctaPrimary,
    ctaSecondary,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
        <Field label="Hero Video">
          <MediaImageField
            label="Select Hero Video"
            value={heroVideo}
            onChange={setHeroVideo}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text}
          url={heroPrimary.url}
          newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondary.text}
          url={heroSecondary.url}
          newTab={heroSecondary.newTab}
          onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroSecondary, "newTab", v)}
        />

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Trust Badges</p>
          <button
            onClick={() =>
              setTrustBadges((s) => [...s, { icon: "", title: "", sub: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Badge
          </button>
        </div>
        {trustBadges.map((b, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Badge {i + 1}</p>
              {trustBadges.length > 1 && (
                <button
                  onClick={() =>
                    setTrustBadges((s) => s.filter((_, idx) => idx !== i))
                  }
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <Field label="Icon">
              <SelectInput
                value={b.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setTrustBadges((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={b.title}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Sub">
                <TextInput
                  value={b.sub}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, sub: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* SOLUTION SNAPSHOT */}
      <CollapsibleSection
        title="Solution Snapshot"
        active={snapActive}
        onToggleActive={setSnapActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={snapBadgeText}
            onChange={(e) => setSnapBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={snapHeading}
            onChange={(e) => setSnapHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={snapHeadingEmphasis}
            onChange={(e) => setSnapHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={snapDescription}
            onChange={(e) => setSnapDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={snapCTAText}
              onChange={(e) => setSnapCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={snapCTALink}
              onChange={(e) => setSnapCTALink(e.target.value)}
            />
          </Field>
        </div>
      </CollapsibleSection>

      {/* SOLUTIONS BY TECHNOLOGY */}
      <CollapsibleSection
        title="Solutions by Technology"
        active={techActive}
        onToggleActive={setTechActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={techBadgeText}
            onChange={(e) => setTechBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={techHeading}
            onChange={(e) => setTechHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={techHeadingEmphasis}
            onChange={(e) => setTechHeadingEmphasis(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setTechCards((s) => [
                ...s,
                { title: "", description: "", href: "", icon: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {techCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={techCards.length}
                  onMove={(f, t) => setTechCards(moveItem(techCards, f, t))}
                />
                {techCards.length > 1 && (
                  <button
                    onClick={() =>
                      setTechCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setTechCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setTechCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Link (href)">
              <TextInput
                value={c.href}
                onChange={(e) =>
                  setTechCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, href: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setTechCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* SOLUTIONS BY ROLE */}
      <CollapsibleSection
        title="Solutions by Role"
        active={roleActive}
        onToggleActive={setRoleActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={roleBadgeText}
            onChange={(e) => setRoleBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={roleHeading}
            onChange={(e) => setRoleHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={roleHeadingEmphasis}
            onChange={(e) => setRoleHeadingEmphasis(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Role Cards</p>
          <button
            onClick={() =>
              setRoleCards((s) => [...s, { title: "", href: "", icon: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Role
          </button>
        </div>
        {roleCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Role {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={roleCards.length}
                  onMove={(f, t) => setRoleCards(moveItem(roleCards, f, t))}
                />
                {roleCards.length > 1 && (
                  <button
                    onClick={() =>
                      setRoleCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setRoleCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Link (href)">
              <TextInput
                value={c.href}
                onChange={(e) =>
                  setRoleCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, href: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setRoleCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* SOLUTIONS BY INDUSTRY */}
      <CollapsibleSection
        title="Solutions by Industry"
        active={indActive}
        onToggleActive={setIndActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={indBadgeText}
            onChange={(e) => setIndBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={indHeading}
            onChange={(e) => setIndHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={indHeadingEmphasis}
            onChange={(e) => setIndHeadingEmphasis(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Industry Cards</p>
          <button
            onClick={() =>
              setIndCards((s) => [
                ...s,
                { title: "", href: "", icon: "", gradient: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Industry
          </button>
        </div>
        {indCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Industry {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={indCards.length}
                  onMove={(f, t) => setIndCards(moveItem(indCards, f, t))}
                />
                {indCards.length > 1 && (
                  <button
                    onClick={() =>
                      setIndCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Link (href)">
              <TextInput
                value={c.href}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, href: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Gradient (Tailwind classes)">
              <TextInput
                value={c.gradient}
                placeholder="from-orange-400 to-amber-500"
                onChange={(e) =>
                  setIndCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, gradient: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* WHY THINKPROCESS */}
      <CollapsibleSection
        title="Why ThinkProcess"
        active={whyActive}
        onToggleActive={setWhyActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={whyBadgeText}
            onChange={(e) => setWhyBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={whyHeading}
            onChange={(e) => setWhyHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={whyHeadingEmphasis}
            onChange={(e) => setWhyHeadingEmphasis(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Cards</p>
          <button
            onClick={() =>
              setWhyCards((s) => [...s, { title: "", description: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Card
          </button>
        </div>
        {whyCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Card {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={whyCards.length}
                  onMove={(f, t) => setWhyCards(moveItem(whyCards, f, t))}
                />
                {whyCards.length > 1 && (
                  <button
                    onClick={() =>
                      setWhyCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setWhyCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}
